import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import type { Campaign, Importer } from '@feathr/blackbox';
import { toast } from '@feathr/components';
import { getContactsOnlyImportUrl } from '@feathr/extender/App/DataImportsPage/DataImportPage/DataImportPage.utils';
import { useLocalUrl, useStore, useUser } from '@feathr/extender/state';
import { moment, TimeFormat } from '@feathr/hooks';

export interface IUseContactsImportResult {
  /** Creates a draft person import, or shows an error toast if the create fails */
  addImporter: () => Promise<void>;
  /** The contacts-only wizard URL for the import `addImporter` created, or `false` if there is none */
  getRedirect: () => string | false;
}

/**
 * Starts an import from a campaign's Import contacts button.
 *
 * Encapsulates:
 * - Creating the draft person import
 * - The wizard URL the button redirects to after the campaign is saved
 *
 * @param campaign - The campaign the import is started from
 * @returns The `onConfirm` and `redirect` handlers for the `ConfirmButton`
 * @example
 * const { addImporter, getRedirect } = useContactsImport(campaign);
 * <ConfirmButton onConfirm={addImporter} redirect={getRedirect} />
 */
export function useContactsImport(campaign: Campaign): IUseContactsImportResult {
  const { Importers } = useStore();
  const localUrl = useLocalUrl();
  const user = useUser();
  const { t } = useTranslation();
  const importerRef = useRef<Importer | undefined>();

  async function addImporter(): Promise<void> {
    importerRef.current = undefined;
    try {
      const importerAttrs: Record<string, string> = {
        source_campaign: campaign.id,
        created_by: user.id,
        kind: 'person',
        state: 'draft',
        name: t('Import {{date}}', { date: moment().format(TimeFormat.isoDate) }),
      };

      const newImporter = Importers.create(importerAttrs);
      const response = await Importers.add(newImporter, { validate: false });
      if (response.isErrored) {
        throw response.error;
      }
      importerRef.current = response;
    } catch {
      toast(t('There was an error creating the import.'), { type: 'error' });
    }
  }

  function getRedirect(): string | false {
    if (!importerRef.current) {
      return false;
    }
    return getContactsOnlyImportUrl(localUrl(importerRef.current.getItemUrl()));
  }

  return { addImporter, getRedirect };
}
